/**
 * Main WarehouseTable Component
 * 
 * A complete, reusable table component with all the professional styling
 * and functionality from the ItemLocationBalance table, but generic and
 * type-safe for any data type.
 */

import React, { useMemo } from 'react';
import {
  Search,
  Filter,
  Download,
  RefreshCw,
  Settings,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  AlertCircle,
} from 'lucide-react';
import { useWarehouseTable } from '../hooks/useWarehouseTable';
import { ResizableHeader } from './ResizableHeader';
import { ColumnDropdownFilter } from './ColumnDropdownFilter';
import { ColumnConfigModal } from './ColumnConfigModal';
import { SelectionCheckbox } from './SelectionCheckbox';
import { defaultFormatter } from '../utils/formatters';
import { getDistinctValues } from '../utils/tableUtils';
import type { WarehouseTableProps, TableDataRow, ColumnDefinition, SortDirection } from '../types/table';
import '../styles/warehouse-table.css';

export function WarehouseTable<T extends TableDataRow>({
  data,
  columns: initialColumns,
  config = {},
  selectedRows,
  onSelectionChange,
  getRowId,
  actions = {},
  rowActions = [],
  tableActions = [],
  summaryCalculations = [],
  loading = false,
  error,
  className = '',
  'aria-label': ariaLabel = 'Data table'
}: WarehouseTableProps<T>) {
  
  const {
    tableState,
    handleSort,
    handleFilter,
    handlePageChange,
    handlePageSizeChange,
    handleColumnToggle,
    handleColumnReorder,
    handleColumnResize,
    handleExport,
    handleReset,
    handleRowSelect,
    handleSelectAll,
    handleSelectionClear,
    summaryData,
    visibleColumns
  } = useWarehouseTable({
    data,
    columns: initialColumns,
    config,
    summaryCalculations,
    selectedRows,
    onSelectionChange,
    getRowId
  });

  const [showColumnConfig, setShowColumnConfig] = React.useState(false);

  // Render sort icon
  const renderSortIcon = (columnKey: keyof T) => {
    const { column: sortColumn, direction } = tableState.sort;
    
    if (sortColumn !== columnKey) {
      return <ChevronsUpDown className="w-3 h-3 text-slate-400" />;
    }
    
    if (direction === 'asc') {
      return <ChevronUp className="w-3 h-3 text-slate-600" />;
    } else if (direction === 'desc') {
      return <ChevronDown className="w-3 h-3 text-slate-600" />;
    } else {
      return <ChevronsUpDown className="w-3 h-3 text-slate-400" />;
    }
  };

  // Format cell value
  const formatCellValue = (value: any, column: ColumnDefinition<T>, row: T): string => {
    if (column.formatter) {
      return column.formatter(value, row);
    }
    return defaultFormatter(value, column.type);
  };

  // Handle column filter changes
  const handleColumnFilterChange = (columnKey: string, value: string) => {
    const newFilters = {
      ...tableState.filters.columnFilters,
      [columnKey]: value
    };
    handleFilter({ columnFilters: newFilters });
  };

  // Handle dropdown filter changes
  const handleDropdownFilterChange = (columnKey: string, values: string[]) => {
    const newFilters = {
      ...tableState.filters.dropdownFilters,
      [columnKey]: values
    };
    handleFilter({ dropdownFilters: newFilters });
  };

  // Get distinct values for dropdown filters
  const getColumnDistinctValues = (columnKey: keyof T) => {
    return getDistinctValues(tableState.filteredData, columnKey);
  };

  if (loading) {
    return (
      <div className="warehouse-table-loading">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-500 mx-auto mb-2" />
        <p>Laster data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="warehouse-table-empty">
        <AlertCircle className="w-8 h-8 text-red-300 mx-auto mb-2" />
        <h3>Feil ved lasting av data</h3>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Header with controls */}
      {(config.showGlobalSearch || config.showExport || config.showColumnConfig || tableActions.length > 0) && (
        <div className="sticky top-0 z-20 bg-white border-b border-slate-300 px-4 py-2">
          <div className="flex items-center justify-end">
            <div className="flex items-center gap-2">
              {/* Global Search */}
              {config.showGlobalSearch && (
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <input
                    type="text"
                    value={tableState.filters.globalSearch || ''}
                    onChange={(e) => handleFilter({ globalSearch: e.target.value })}
                    placeholder="Søk..."
                    className="pl-8 pr-3 py-1.5 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    style={{ width: '300px' }}
                  />
                </div>
              )}

              {/* Table Actions */}
              {tableActions.map((action) => (
                <button
                  key={action.id}
                  onClick={() => action.onClick(tableState.filteredData, [])}
                  disabled={action.disabled?.(tableState.filteredData, [])}
                  className="px-2 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-300 rounded hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {action.icon && <action.icon className="w-3.5 h-3.5 mr-1" />}
                  {action.label}
                </button>
              ))}

              {/* Export Button */}
              {config.showExport && (
                <button
                  onClick={handleExport}
                  className="px-2 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-300 rounded hover:bg-slate-50 transition-colors"
                >
                  <Download className="w-3.5 h-3.5 mr-1" />
                  Eksporter
                </button>
              )}

              {/* Column Config Button */}
              {config.showColumnConfig && (
                <button
                  onClick={() => setShowColumnConfig(true)}
                  className="px-2 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-300 rounded hover:bg-slate-50 transition-colors"
                >
                  <Settings className="w-3.5 h-3.5 mr-1" />
                  Kolonner
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Table Container */}
      <div className="warehouse-table-container flex-1">
        <table 
          className={`warehouse-table ${config.compactMode ? 'compact-mode' : ''}`}
          aria-label={ariaLabel}
        >
          <thead className={config.stickyHeader ? 'sticky top-0' : ''}>
            {/* Column Headers */}
            <tr>
              {/* Selection Column Header */}
              {config.allowRowSelection && (
                <th className="px-2 py-1.5 w-10 text-center text-xs font-medium text-slate-700 bg-blue-100 border-r border-slate-300">
                  <SelectionCheckbox
                    checked={tableState.selection.isSelectAllChecked}
                    indeterminate={tableState.selection.isSelectAllIndeterminate}
                    onChange={handleSelectAll}
                    aria-label="Select all rows"
                  />
                </th>
              )}
              {visibleColumns.map((column) => (
                <ResizableHeader
                  key={column.id}
                  column={column}
                  onResize={handleColumnResize}
                  className={`px-2 py-1.5 text-${column.alignment} text-xs font-medium text-slate-700 cursor-pointer hover:bg-blue-100 transition-colors border-r border-slate-300 bg-blue-100`}
                  onClick={column.sortable ? () => handleSort(column.key) : undefined}
                >
                  <div className={`flex items-center gap-1 ${
                    column.alignment === 'center' ? 'justify-center' : 
                    column.alignment === 'right' ? 'justify-end' : 'justify-start'
                  }`}>
                    <span className="flex-1">{column.label}</span>
                    <div className="flex items-center gap-0.5">
                      {(column.filterType === 'dropdown' || column.filterType === 'both') && (
                        <ColumnDropdownFilter
                          column={column}
                          data={tableState.data}
                          selectedValues={tableState.filters.dropdownFilters?.[String(column.key)] || []}
                          onSelectionChange={(values) => handleDropdownFilterChange(String(column.key), values)}
                          formatValue={column.formatter}
                        />
                      )}
                      {column.sortable && renderSortIcon(column.key)}
                    </div>
                  </div>
                </ResizableHeader>
              ))}
            </tr>

            {/* Filter Row - Only text input fields */}
            {config.showFilters && (
              <tr className="bg-blue-100 border-t border-slate-300">
                {/* Empty cell for selection column */}
                {config.allowRowSelection && (
                  <th className="px-2 py-0.5 w-10 border-r border-slate-300 bg-blue-100">
                    {/* Empty cell for selection column in filter row */}
                  </th>
                )}
                {visibleColumns.map((column) => (
                  <th key={`filter-${column.id}`} className="px-1 py-0.5 border-r border-slate-300 bg-blue-100">
                    <input
                      type="text"
                      placeholder="..."
                      value={tableState.filters.columnFilters?.[String(column.key)] || ''}
                      onChange={(e) => handleColumnFilterChange(String(column.key), e.target.value)}
                      className="warehouse-table-filter-input"
                      style={{ width: '100%', minWidth: `${column.width - 4}px` }}
                    />
                  </th>
                ))}
              </tr>
            )}
          </thead>

          <tbody>
            {tableState.paginatedData.length > 0 ? (
              tableState.paginatedData.map((item, idx) => (
                <tr
                  key={`${item.id}-${idx}`}
                  className={`hover:bg-blue-100 transition-colors duration-150 ${
                    idx % 2 === 0 ? 'bg-white' : 'bg-blue-50'
                  }`}
                  onClick={() => actions.onRowClick?.(item, idx)}
                  onDoubleClick={() => actions.onRowDoubleClick?.(item, idx)}
                  style={{ cursor: actions.onRowClick ? 'pointer' : 'default' }}
                >
                  {/* Selection Column Cell */}
                  {config.allowRowSelection && (
                    <td className="px-2 py-1 w-10 text-center border-r border-slate-200">
                      <SelectionCheckbox
                        checked={tableState.selection.selectedRows.has((getRowId || ((row: T) => row.id))(item))}
                        onChange={(checked) => handleRowSelect((getRowId || ((row: T) => row.id))(item), checked)}
                        aria-label={`Select row ${idx + 1}`}
                      />
                    </td>
                  )}
                  {visibleColumns.map((column) => (
                    <td
                      key={`${item.id}-${column.id}`}
                      className={`px-2 py-1 text-xs border-r border-slate-200 whitespace-nowrap ${
                        column.alignment === 'center' ? 'text-center' : 
                        column.alignment === 'right' ? 'text-right' : 'text-left'
                      }`}
                      style={{ width: `${column.width}px` }}
                      title={column.key === 'description' || column.type === 'text' ? String(item[column.key] || '') : undefined}
                    >
                      {column.renderer ? 
                        column.renderer(item[column.key], item) :
                        formatCellValue(item[column.key], column, item)
                      }
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={visibleColumns.length + (config.allowRowSelection ? 1 : 0)} className="warehouse-table-empty">
                  <Search className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <h3>Ingen rader matcher filtrene</h3>
                  <p>Prøv å justere filtrene dine eller slette innhold i kolonnefilterene</p>
                </td>
              </tr>
            )}
          </tbody>

          {/* Summary Footer */}
          {config.showSummary && summaryCalculations.length > 0 && (
            <tfoot className={`${config.stickyFooter ? 'sticky bottom-0' : ''} bg-blue-100`}>
              <tr>
                {/* Empty cell for selection column in summary */}
                {config.allowRowSelection && (
                  <td className="px-2 py-1 w-10 text-xs font-medium border-r border-slate-300">
                    {/* Empty cell for selection column in summary row */}
                  </td>
                )}
                {visibleColumns.map((column) => {
                  const summaryValue = summaryData[String(column.key)];
                  const summaryConfig = summaryCalculations.find(calc => calc.column === column.key);
                  
                  return (
                    <td
                      key={`summary-${column.id}`}
                      className={`px-2 py-1 text-xs font-medium border-r border-slate-300 ${
                        column.alignment === 'center' ? 'text-center' : 
                        column.alignment === 'right' ? 'text-right' : 'text-left'
                      } text-slate-700 bg-blue-100`}
                    >
                      {summaryValue !== undefined ? (
                        summaryConfig?.formatter ? 
                          summaryConfig.formatter(summaryValue) :
                          defaultFormatter(summaryValue, column.type)
                      ) : ''}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Pagination */}
      {config.showPagination && (
        <div className="sticky bottom-0 bg-white border-t border-slate-300 px-4 py-2">
          <div className="flex items-center justify-between text-xs text-slate-600">
            <div className="flex items-center gap-4">
              <span>
                Viser {((tableState.pagination.currentPage - 1) * tableState.pagination.pageSize) + 1}-{Math.min(tableState.pagination.currentPage * tableState.pagination.pageSize, tableState.pagination.totalRows)} av {tableState.pagination.totalRows} rader
              </span>
              
              <div className="flex items-center gap-2">
                <span>Rader per side:</span>
                <select
                  value={tableState.pagination.pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="px-1 py-0.5 border border-slate-300 rounded text-xs"
                >
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value={tableState.pagination.totalRows}>Alle</option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(1)}
                disabled={tableState.pagination.currentPage === 1}
                className="p-1 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronsLeft className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => handlePageChange(tableState.pagination.currentPage - 1)}
                disabled={tableState.pagination.currentPage === 1}
                className="p-1 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              
              <span className="px-2 py-1 text-xs">
                Side {tableState.pagination.currentPage} av {tableState.pagination.totalPages}
              </span>
              
              <button
                onClick={() => handlePageChange(tableState.pagination.currentPage + 1)}
                disabled={tableState.pagination.currentPage === tableState.pagination.totalPages}
                className="p-1 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => handlePageChange(tableState.pagination.totalPages)}
                disabled={tableState.pagination.currentPage === tableState.pagination.totalPages}
                className="p-1 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronsRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Column Configuration Modal */}
      {showColumnConfig && (
        <ColumnConfigModal
          isOpen={showColumnConfig}
          onClose={() => setShowColumnConfig(false)}
          columns={tableState.columns}
          onToggleVisibility={handleColumnToggle}
          onReorder={handleColumnReorder}
          onResetToDefault={handleReset}
        />
      )}
    </div>
  );
}